import type { CustomerMessage, CustomerMessageTimeline } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { Mail, MessageCircle, MessageSquare } from "lucide-react";
import { useState } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { Drawer } from "../primitives/Drawer";
import { SkeletonText } from "../primitives/Skeleton";
import type { Tone } from "../styles";
import { EmptyState, ErrorState } from "./page/states";
import { StatusChip } from "./StatusChip";

/**
 * The Messages panel of an order (EP12-S09, DA-12-09): every message its
 * customer was sent, newest first — the channel, where it is (`queued`,
 * `held` until the morning, `sent`, `delivered`, `read`, `failed`), when, the
 * parcel it is about (`1 of 2`) — and a View that opens the text exactly as
 * the customer received it, right to left when it went in Arabic.
 *
 * The Admin Console's order page and the Store Portal's order detail both draw
 * it; each fetches from its own door and hands the answer here.
 */

/** A message's status as a chip tone. */
export const MESSAGE_TONES: Readonly<Record<string, Tone>> = {
	queued: "neutral",
	held: "neutral",
	dispatched: "neutral",
	sent: "info",
	delivered: "success",
	read: "success",
	failed: "danger",
	dead: "danger",
	suppressed: "warning",
	// ADJ-B02-2: not on WhatsApp while SMS is off — nothing went, nothing is wrong.
	skipped: "neutral",
};

const CHANNEL_ICONS = { whatsapp: MessageCircle, sms: MessageSquare, email: Mail } as const;

/** When the row says it happened: sent, else the morning it waits for, else written. */
export function messageTime(m: CustomerMessage): { at: string; held: boolean } {
	if (m.sentAt) {
		return { at: m.sentAt, held: false };
	}
	if (m.status === "held" && m.scheduledFor) {
		return { at: m.scheduledFor, held: true };
	}
	return { at: m.createdAt, held: false };
}

export interface CustomerMessagesPanelProps {
	timeline: CustomerMessageTimeline | undefined;
	error?: unknown;
	onRetry?: () => void;
	t: Translate;
	/** Formats an instant in the reader's zone and language. */
	formatTime: (iso: string) => string;
}

export function CustomerMessagesPanel({
	timeline,
	error,
	onRetry,
	t,
	formatTime,
}: CustomerMessagesPanelProps) {
	const [open, setOpen] = useState<CustomerMessage | null>(null);

	let body: React.ReactNode;
	if (error !== undefined && error !== null) {
		body = (
			<ErrorState error={error} t={t} compact {...(onRetry === undefined ? {} : { onRetry })} />
		);
	} else if (timeline === undefined) {
		body = <SkeletonText lines={3} />;
	} else if (timeline.messages.length === 0) {
		body = <EmptyState title={t("orders.customerMessages.empty")} illustration="none" />;
	} else {
		body = (
			<ul className="flex flex-col divide-y divide-border">
				{timeline.messages.map((m) => {
					const { at, held } = messageTime(m);
					const channelIcon =
						CHANNEL_ICONS[m.channel as keyof typeof CHANNEL_ICONS] ?? MessageSquare;
					return (
						<li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[13px]">
							<Icon
								as={channelIcon}
								size={16}
								className="text-fg-muted"
								title={t(`orders.customerMessages.channel.${m.channel}`)}
							/>
							<span className="font-semibold text-fg">
								{t(`orders.customerMessages.event.${m.eventType}`)}
							</span>
							{m.shipmentIndex ? <span className="text-fg-muted">{m.shipmentIndex}</span> : null}
							<StatusChip tone={MESSAGE_TONES[m.status] ?? "neutral"} size="sm">
								{t(`orders.customerMessages.status.${m.status}`)}
							</StatusChip>
							<span className="text-fg-muted">
								{held
									? t("orders.customerMessages.heldUntil", { time: formatTime(at) })
									: formatTime(at)}
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="ms-auto"
								disabled={!m.body}
								onClick={() => setOpen(m)}
							>
								{t("orders.customerMessages.view")}
							</Button>
						</li>
					);
				})}
			</ul>
		);
	}

	const contact = timeline?.contact;
	return (
		<section aria-labelledby="customer-messages-title" className="card p-4">
			<div className="mb-2 flex flex-wrap items-center gap-2">
				<h2 id="customer-messages-title" className="text-[15px] font-bold text-fg">
					{t("orders.customerMessages.title")}
				</h2>
				{contact?.waCapable === false && (
					<StatusChip tone="warning" size="sm">
						{t("orders.customerMessages.notOnWhatsApp")}
					</StatusChip>
				)}
				{contact?.optedOut === true && (
					<StatusChip tone="neutral" size="sm">
						{t("orders.customerMessages.optedOut")}
					</StatusChip>
				)}
			</div>
			{body}
			<Drawer
				open={open !== null}
				onOpenChange={(next) => !next && setOpen(null)}
				title={open === null ? "" : t(`orders.customerMessages.event.${open.eventType}`)}
				description={
					open === null
						? undefined
						: `${t(`orders.customerMessages.channel.${open.channel}`)} · ${t(
								`orders.customerMessages.status.${open.status}`,
							)}`
				}
				closeLabel={t("ui.actions.close")}
			>
				{open !== null && (
					<div className="flex flex-col gap-3">
						{open.subject ? (
							<p className="font-semibold text-fg" dir={open.locale === "ar" ? "rtl" : "ltr"}>
								{open.subject}
							</p>
						) : null}
						<p
							data-testid="customer-message-body"
							dir={open.locale === "ar" ? "rtl" : "ltr"}
							lang={open.locale}
							className="whitespace-pre-wrap rounded-md bg-bg-subtle p-3 text-[13.5px] text-fg"
						>
							{open.body}
						</p>
						{open.failureCode ? (
							<p className="text-[12.5px] text-danger-fg">
								{t("orders.customerMessages.failure", { code: open.failureCode })}
							</p>
						) : null}
					</div>
				)}
			</Drawer>
		</section>
	);
}
